import React, { useRef, useState } from "react";

import { css } from "@emotion/css";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { FileModel, UploadedFileType } from "@/models/FileModel.ts";
import { Patient } from "@/models/PatientModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Loader } from "@/uiKit/atoms/Loader.tsx";
import { suffixId } from "@/uiKit/atoms/utils/ids.ts";

type UploadButtonProps = {
  id?: string;
  onFileChange: (file?: FileModel) => void;
  fileType: UploadedFileType;
  patientInfo: Pick<Patient, "givenName" | "familyName">;
  button?: {
    variant?: "text" | "outlined" | "contained";
    text?: string;
  };
  defaultValue?: FileModel;
  hasTrash?: boolean;
  displayFilename?: boolean;
  icon?: React.ReactNode;
  className?: string;
  buttonClassName?: string;
};

export const UploadButton = ({
  id,
  onFileChange,
  fileType,
  patientInfo,
  defaultValue,
  button,
  hasTrash = false,
  displayFilename = true,
  icon,
  className,
  buttonClassName,
}: UploadButtonProps) => {
  const styles = useStyles(makeStyles);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadFile = Queries.practitioner.useUploadFile();

  const [hasBeenDeleted, setHasBeenDeleted] = useState(false);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const uploadFileHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return;
    }
    setHasBeenDeleted(false);

    const file = event.target.files[0];
    uploadFile.mutate(
      {
        file,
        type: fileType,
        patient_firstname: patientInfo.givenName,
        patient_lastname: patientInfo.familyName,
      },
      {
        onSuccess: data => {
          onFileChange(data);
        },
      },
    );
  };

  const filename =
    hasBeenDeleted || !displayFilename ? null : fileInputRef.current?.files?.[0]?.name || defaultValue?.name;

  const Icon = () =>
    uploadFile.status == "pending" ? <Loader size="S" /> : (icon ?? <Icons.fileUpload className={styles.icon} />);

  return (
    <div id={id} className={className} data-testid="upload-button">
      <Button
        variant={button?.variant ?? "outlined"}
        data-action="common.upload.select"
        tabIndex={-1}
        onClick={handleButtonClick}
        endIcon={filename && !button?.text && <Icon />}
        className={buttonClassName}
      >
        {filename ? filename : button?.text ? button.text : <Icon />}
      </Button>
      <input
        id={suffixId(id, "input")}
        type="file"
        className={styles.hidden}
        ref={fileInputRef}
        onChange={uploadFileHandler}
      />
      {hasTrash && filename && (
        <Button
          variant="text"
          data-action="common.upload.delete"
          onClick={() => {
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
            setHasBeenDeleted(true);
            onFileChange({
              name: "",
              url: "",
              path: "",
            });
          }}
          startIcon={<Icons.trash className={styles.icon} />}
        />
      )}
    </div>
  );
};

const makeStyles = (_theme: Theme) => ({
  hidden: css`
    display: none;
  `,
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
